import { useCallback, useEffect, useState } from 'react';
import { translate } from '../i18n/i18n';
import {
  daysAgo,
  downloadReportCsv,
  fetchReport,
  isoDay,
  type Report,
} from '../reports/reports';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { PlanProgress } from '../planner/ProgressView';
import DateField from '../components/DateField';

const PERIODS = [
  { label: 'This week', days: 6 },
  { label: 'Four weeks', days: 27 },
  { label: 'Three months', days: 89 },
] as const;

function seconds(ms: number | null): string {
  return ms === null ? '—' : translate('{seconds}s', { seconds: (ms / 1000).toFixed(1) });
}

export default function ReportPage() {
  const t = useT();
  const { learnerId } = useLearner();
  const [from, setFrom] = useState<string>(daysAgo(6));
  const [to, setTo] = useState<string>(isoDay(new Date()));
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await fetchReport(learnerId, from, to));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not build that report.'));
    } finally {
      setLoading(false);
    }
  }, [learnerId, from, to]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await downloadReportCsv(learnerId, from, to);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That file could not be made just now.'));
    } finally {
      setSaving(false);
    }
  }

  function pick(days: number) {
    setFrom(daysAgo(days));
    setTo(isoDay(new Date()));
  }

  return (
    <section aria-labelledby="report-h">
      <div className="week-bar">
        <h2 id="report-h" className="week-title">
          {report ? t("{name}'s progress", { name: report.learnerName }) : t('Progress report')}
        </h2>
        <div className="toolbar">
          {PERIODS.map((p) => (
            <button key={p.label} className="btn secondary" onClick={() => pick(p.days)}>
              {t(p.label)}
            </button>
          ))}
          <button className="btn" onClick={() => void save()} disabled={saving || !report}>
            {saving ? t('Preparing…') : t('Save as a file')}
          </button>
        </div>
      </div>

      {error && <div className="error" role="alert">{error}</div>}

      <div className="card">
        <div className="row">
          <div className="field">
            <label htmlFor="report-from">{t('From')}</label>
            <DateField id="report-from" value={from} onChange={(v) => setFrom(v ?? '')} />
          </div>
          <div className="field">
            <label htmlFor="report-to">{t('To')}</label>
            <DateField id="report-to" value={to} onChange={(v) => setTo(v ?? '')} />
          </div>
        </div>
      </div>

      {loading && !report ? (
        <p className="muted">{t('Loading…')}</p>
      ) : report ? (
        <>
          <div className="card">
            <h2>{t('Over this period')}</h2>
            <div className="row">
              <div className="field">
                <span className="field-label">{t('Right first time')}</span>
                <strong className="figure">{report.percent}%</strong>
                <span className="field-hint">{t('{correct} of {trials} tries', { correct: report.correct, trials: report.trials })}</span>
              </div>
              <div className="field">
                <span className="field-label">{t('Programs worked on')}</span>
                <strong className="figure">{report.programsPractised}</strong>
              </div>
              <div className="field">
                <span className="field-label">{t('On the maintenance list')}</span>
                <strong className="figure">{report.programsOnMaintenance}</strong>
              </div>
              <div className="field">
                <span className="field-label">{t('Rewards given')}</span>
                <strong className="figure">{report.rewards}</strong>
              </div>
            </div>
            <p className="field-hint">
              {t('How much was right first time matters more than how much was done. A longer sitting always produces more tries; it does not always produce more learning.')}
            </p>
          </div>

          <div className="card">
            <h2>{t('Program by program')}</h2>
            {report.lines.length === 0 ? (
              <p className="muted">{t('There are no programs for this learner yet.')}</p>
            ) : (
              <div className="table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('Program')}</th>
                      <th scope="col">{t('List')}</th>
                      <th scope="col" className="num">{t('Right first time')}</th>
                      <th scope="col" className="num">{t('Tries')}</th>
                      <th scope="col" className="num">{t('On their own')}</th>
                      <th scope="col" className="num">{t('Answer time')}</th>
                      <th scope="col" className="num">{t('Rewards')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lines.map((l) => (
                      <tr key={l.programId}>
                        <td>{l.programName}</td>
                        <td>{l.track === 'MAINTENANCE' ? t('Maintenance') : t('Current')}</td>
                        <td className="num">{l.trials === 0 ? '—' : `${l.percent}%`}</td>
                        <td className="num">{l.trials}</td>
                        <td className="num">
                          {l.probePercent === null ? '—' : `${l.probePercent}%`}
                        </td>
                        <td className="num">{seconds(l.averageMs)}</td>
                        <td className="num">{l.rewards}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="field-hint">
              {t('A dash means nothing was recorded for that column in this period. Programs nobody worked on are listed too, because that is worth seeing.')}
            </p>
          </div>

          <div className="card">
            <h2>{t('Plan progress')}</h2>
            <PlanProgress learnerId={learnerId} from={from} to={to} />
          </div>
        </>
      ) : null}
    </section>
  );
}
